import { useState, useEffect, useRef } from 'react';
import { extractOptions } from 'src/util/helpers';
import { getFileUrl } from '../Util';

const useMediaOptions = ({ exercise, hasAudio }) => {
  const audioRef = useRef(null);
  const [mediaList, setMediaList] = useState([]);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const animationRefs = useRef({});
  const [isPlaying, setIsPlaying] = useState(false);

  // Initialize media list
  useEffect(() => {
    setMediaList(extractOptions(exercise?.options) || []);
  }, [exercise]);

  const stopAllAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.removeEventListener('ended', handleAudioEnd);
      audioRef.current = null;
    }
    stopAllAnimations();
    setCurrentlyPlaying(null);
    setIsPlaying(false);
  };

  const stopAllAnimations = () => {
    Object.values(animationRefs.current).forEach(anim => {
      try {
        anim?.stop();
      } catch (e) {
        console.warn('Animation stop error:', e);
      }
    });
  };

  const handleAudioEnd = () => {
    stopAllAnimations();
    setCurrentlyPlaying(null);
    setIsPlaying(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAllAudio();
      animationRefs.current = {};
    };
  }, []);

  const registerAnimationRef = (key, ref) => {
    if (ref) {
      animationRefs.current[key] = ref;
    }
  };

  const unregisterAnimationRef = (key) => {
    delete animationRefs.current[key];
  };

  const onAudioPlay = (url, source) => {
    if (!url || !hasAudio) return;

    try {
      // Stop any currently playing audio
      stopAllAudio();

      const audio = new Audio(getFileUrl(url));
      audio.addEventListener('ended', handleAudioEnd);
      audio.addEventListener('error', () => {
        console.error('Audio playback failed');
        handleAudioEnd();
      });
      
      audio.play()
        .then(() => {
          audioRef.current = audio;
          setCurrentlyPlaying(source);
          setIsPlaying(true);
          
          if (source && animationRefs.current[source]) {
            animationRefs.current[source]?.play();
          }
        })
        .catch(error => {
          console.error('Audio play error:', error);
          handleAudioEnd();
        });

    } catch (error) {
      console.error('Audio initialization error:', error);
      handleAudioEnd();
    }
  };

  return {
    mediaList,
    setMediaList,
    currentlyPlaying,
    isPlaying,
    onAudioPlay,
    stopAllAudio,
    registerAnimationRef,
    unregisterAnimationRef,
    audioControl: {
      currentlyPlaying,
      isPlaying,
      onAudioPlay,
      stopAllAudio
    }
  };
};

export default useMediaOptions;